import { useCallback, useEffect, useMemo, useState } from "react";
import { useProvisionalPositionEditor } from "@/pages/dokumente/useProvisionalPositionEditor";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { TextInput, TextareaInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import { calcDocument, formatCurrency, groupTaxByRate } from "@/lib/documentCalc";
import {
  DOKUMENT_TYP_LABELS,
  filterGdLohnartenForUser,
  getGdLohnarten,
  type DokumentTyp,
  type GdLohnart,
} from "@/lib/dokumente";
import {
  createGdVorlageVersion,
  formatVorlageGueltigAb,
  getGdVorlage,
  updateGdVorlage,
} from "@/lib/gdVorlagen";
import { toast } from "@/lib/toast";
import {
  DOCUMENT_EDITOR_NESTED_OVERLAY_Z,
  DOCUMENT_EDITOR_OVERLAY_Z,
} from "@/pages/dokumente/documentEditorLayers";
import { DocumentMergedPositionEditDialog } from "@/pages/dokumente/DocumentMergedPositionEditDialog";
import {
  DocumentPositionCanvas,
  DocumentPositionCanvasPane,
  DocumentPositionCanvasSidebar,
} from "@/pages/dokumente/DocumentPositionCanvas";
import { DocumentPositionEditDialog } from "@/pages/dokumente/DocumentPositionEditDialog";
import {
  DocumentTemplateMetaFields,
  type DocumentTemplateMetaValue,
} from "@/pages/dokumente/DocumentTemplateMetaFields";
import {
  editorPositionsToTemplatePositions,
  templatePositionsToEditorPositions,
  type GdTemplateVersionSummary,
} from "@/pages/dokumente/documentTemplateModel";
import {
  extractMergedChildFromPositions,
  isMergedContainerPosition,
  type EditorPosition,
} from "@/pages/dokumente/documentPositionModel";

interface DocumentTemplateEditorDialogProps {
  open: boolean;
  templateId: number | null;
  onClose: () => void;
  onSaved: () => void;
  zIndexClass?: string;
}

function defaultGueltigAbNextYear(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  d.setMonth(0, 1);
  return d.toISOString().slice(0, 10);
}

export function DocumentTemplateEditorDialog({
  open,
  templateId,
  onClose,
  onSaved,
  zIndexClass = DOCUMENT_EDITOR_OVERLAY_Z,
}: DocumentTemplateEditorDialogProps) {
  const { user } = useCurrentUser();
  const [activeId, setActiveId] = useState<number | null>(templateId);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creatingVersion, setCreatingVersion] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [beschreibung, setBeschreibung] = useState("");
  const [dokumentTyp, setDokumentTyp] = useState<DokumentTyp>("angebot");
  const [betreff, setBetreff] = useState("");
  const [einleitung, setEinleitung] = useState("");
  const [schlusstext, setSchlusstext] = useState("");
  const [version, setVersion] = useState(1);
  const [gueltigAb, setGueltigAb] = useState("");
  const [versionen, setVersionen] = useState<GdTemplateVersionSummary[]>([]);
  const [showNewVersion, setShowNewVersion] = useState(false);
  const [newVersionDate, setNewVersionDate] = useState(defaultGueltigAbNextYear);
  const [meta, setMeta] = useState<DocumentTemplateMetaValue>({
    schlagworte: [],
    ordner_ids: [],
    sordner_ids: [],
  });
  const [positions, setPositions] = useState<EditorPosition[]>([]);
  const [lohnarten, setLohnarten] = useState<GdLohnart[]>([]);
  const [baseline, setBaseline] = useState("");
  const {
    editLocalId,
    editPosition,
    openPosition,
    closePositionEditor,
    savePosition,
  } = useProvisionalPositionEditor(positions, setPositions);

  const visibleLohnarten = useMemo(
    () => filterGdLohnartenForUser(lohnarten, user),
    [lohnarten, user],
  );

  const snapshot = useMemo(
    () =>
      JSON.stringify({
        name,
        beschreibung,
        betreff,
        einleitung,
        schlusstext,
        gueltigAb,
        meta,
        positions: editorPositionsToTemplatePositions(positions),
      }),
    [beschreibung, betreff, einleitung, gueltigAb, meta, name, positions, schlusstext],
  );
  const dirty = Boolean(baseline) && snapshot !== baseline;

  useEffect(() => {
    if (open) setActiveId(templateId);
  }, [open, templateId]);

  const reload = useCallback(async () => {
    if (!open || activeId == null) return;
    setLoading(true);
    setError(null);
    try {
      const [template, lohn] = await Promise.all([getGdVorlage(activeId), getGdLohnarten()]);
      setName(template.name || "");
      setBeschreibung(template.beschreibung || "");
      setDokumentTyp(template.dokument_typ);
      setBetreff(template.betreff || "");
      setEinleitung(template.einleitung || "");
      setSchlusstext(template.schlusstext || "");
      setVersion(template.version ?? 1);
      setGueltigAb((template.gueltig_ab || "").slice(0, 10));
      setVersionen(template.versionen || []);
      setMeta({
        schlagworte: template.schlagworte || [],
        ordner_ids: template.ordner_ids || [],
        sordner_ids: template.sordner_ids || [],
      });
      const nextPositions = templatePositionsToEditorPositions(template.positionen || []);
      setPositions(nextPositions);
      setLohnarten(lohn || []);
      setBaseline(
        JSON.stringify({
          name: template.name || "",
          beschreibung: template.beschreibung || "",
          betreff: template.betreff || "",
          einleitung: template.einleitung || "",
          schlusstext: template.schlusstext || "",
          gueltigAb: (template.gueltig_ab || "").slice(0, 10),
          meta: {
            schlagworte: template.schlagworte || [],
            ordner_ids: template.ordner_ids || [],
            sordner_ids: template.sordner_ids || [],
          },
          positions: editorPositionsToTemplatePositions(nextPositions),
        }),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Vorlage konnte nicht geladen werden.");
      setPositions([]);
      setBaseline("");
    } finally {
      setLoading(false);
    }
  }, [activeId, open]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!open) {
      closePositionEditor();
      setError(null);
      setBaseline("");
      setShowNewVersion(false);
    }
  }, [open, closePositionEditor]);

  const previewTotals = useMemo(() => calcDocument(positions), [positions]);
  const taxGroups = useMemo(() => groupTaxByRate(positions), [positions]);

  async function handleSave(options?: { close?: boolean }) {
    if (activeId == null) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Bitte einen Namen für die Vorlage angeben.");
      return;
    }
    if (positions.length === 0) {
      setError("Die Vorlage braucht mindestens eine Position.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateGdVorlage(activeId, {
        name: trimmed,
        beschreibung: beschreibung.trim() || null,
        betreff: betreff.trim() || null,
        einleitung: einleitung.trim() || null,
        schlusstext: schlusstext.trim() || null,
        gueltig_ab: gueltigAb || null,
        schlagworte: meta.schlagworte,
        ordner_ids: meta.ordner_ids,
        sordner_ids: meta.sordner_ids,
        positionen: editorPositionsToTemplatePositions(positions),
      });
      toast.success("Vorlage gespeichert.");
      onSaved();
      if (options?.close) {
        onClose();
        return;
      }
      // Dialog offen lassen (z. B. um danach die Version zu wechseln).
      setBaseline(snapshot);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Vorlage konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateVersion() {
    if (activeId == null) return;
    if (!newVersionDate) {
      setError("Bitte das Ablösedatum für die neue Version angeben.");
      return;
    }
    if (dirty) {
      setError("Bitte speichere zuerst die aktuellen Änderungen, bevor du eine neue Version anlegst.");
      return;
    }
    setCreatingVersion(true);
    setError(null);
    try {
      const created = await createGdVorlageVersion(activeId, {
        gueltig_ab: newVersionDate,
        name: name.trim() || undefined,
      });
      toast.success(`Version ${created.version ?? "?"} angelegt.`);
      setShowNewVersion(false);
      setActiveId(created.id);
      onSaved();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Neue Version konnte nicht angelegt werden.",
      );
    } finally {
      setCreatingVersion(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={dirty}
      title={`Vorlage bearbeiten · ${DOKUMENT_TYP_LABELS[dokumentTyp]} · v${version}`}
      description="Positionen und Metadaten der Vorlage – ohne Dokument-Kopfbereich."
      size="4xl"
      zIndexClass={zIndexClass}
      fullScreenMobile
      bodyClassName="!p-0"
      footer={
        <>
          <DialogCancelButton onClose={onClose} disabled={saving || creatingVersion} />
          <Button
            type="button"
            variant="secondary"
            onClick={() => void handleSave({ close: false })}
            loading={saving}
            disabled={loading || creatingVersion}
          >
            Speichern
          </Button>
          <Button
            type="button"
            onClick={() => void handleSave({ close: true })}
            loading={saving}
            disabled={loading || creatingVersion}
          >
            Speichern und Schließen
          </Button>
        </>
      }
    >
      {loading ? (
        <div className="flex items-center gap-2 px-5 py-10 text-sm text-label-secondary">
          <Spinner className="h-4 w-4" />
          Vorlage wird geladen …
        </div>
      ) : (
        <div className="space-y-4 px-5 py-4">
          {error ? <Alert variant="error">{error}</Alert> : null}

          <div className="rounded-xl border border-separator bg-surface px-3 py-3">
            <div className="flex flex-wrap items-end gap-3">
              {versionen.length > 1 ? (
                <label className="block min-w-[10rem] flex-1 space-y-1 text-sm">
                  <span className="text-label-secondary">Version</span>
                  <select
                    className="eh-input w-full"
                    value={activeId ?? ""}
                    disabled={saving || creatingVersion || dirty}
                    onChange={(e) => setActiveId(Number(e.target.value))}
                  >
                    {versionen.map((v) => (
                      <option key={v.id} value={v.id}>
                        Version {v.version}
                        {v.gueltig_ab ? ` · ab ${formatVorlageGueltigAb(v.gueltig_ab)}` : ""}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <div className="text-sm text-label-secondary">
                  Version {version}
                  {gueltigAb ? ` · gültig ab ${formatVorlageGueltigAb(gueltigAb)}` : ""}
                </div>
              )}
              <TextInput
                label="Gültig ab"
                type="date"
                value={gueltigAb}
                onChange={(e) => setGueltigAb(e.target.value)}
                disabled={saving || creatingVersion}
              />
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={saving || creatingVersion || loading}
                onClick={() => {
                  setNewVersionDate(defaultGueltigAbNextYear());
                  setShowNewVersion((openPanel) => !openPanel);
                }}
              >
                Neue Version
              </Button>
            </div>
            <p className="mt-2 text-xs text-label-tertiary">
              Ab dem Datum löst diese Version ältere Versionen derselben Vorlage ab (z.&nbsp;B.
              jährliche Preisanpassung).
            </p>
            {showNewVersion ? (
              <div className="mt-3 flex flex-wrap items-end gap-3 rounded-lg border border-accent/30 bg-accent-soft/30 p-3">
                <TextInput
                  label="Ablösedatum der neuen Version"
                  type="date"
                  value={newVersionDate}
                  onChange={(e) => setNewVersionDate(e.target.value)}
                  disabled={creatingVersion}
                />
                <Button
                  type="button"
                  size="sm"
                  loading={creatingVersion}
                  onClick={() => void handleCreateVersion()}
                >
                  Version anlegen
                </Button>
              </div>
            ) : null}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-3">
              <TextInput
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Name der Vorlage"
                disabled={saving}
              />
              <TextInput
                label="Kurzbeschreibung (optional)"
                value={beschreibung}
                onChange={(e) => setBeschreibung(e.target.value)}
                placeholder="Wofür ist diese Vorlage gedacht?"
                disabled={saving}
              />
              <TextInput
                label="Betreff (optional)"
                value={betreff}
                onChange={(e) => setBetreff(e.target.value)}
                disabled={saving}
              />
              <TextareaInput
                label="Einleitung (optional)"
                value={einleitung}
                onChange={(e) => setEinleitung(e.target.value)}
                disabled={saving}
              />
              <TextareaInput
                label="Schlusstext (optional)"
                value={schlusstext}
                onChange={(e) => setSchlusstext(e.target.value)}
                disabled={saving}
              />
            </div>
            <DocumentTemplateMetaFields value={meta} onChange={setMeta} disabled={saving} />
          </div>

          <DocumentPositionCanvas
            sidebarPlacement="external"
            positions={positions}
            editable
            currency="EUR"
            lohnarten={visibleLohnarten}
            dokumentTyp={dokumentTyp}
            templates={[]}
            templatesLoading={false}
            applyingTemplateId={null}
            templatesEnabled={false}
            onChange={setPositions}
            onOpenPosition={openPosition}
            onApplyTemplate={() => undefined}
            onDeleteTemplate={() => undefined}
            onSaveTemplate={() => undefined}
          >
            <div className="gd-editor-pane space-y-4">
              <DocumentPositionCanvasPane />
              <div className="rounded-xl border border-separator bg-card p-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-label-secondary">Zwischensumme netto</span>
                    <span>{formatCurrency(previewTotals.summe_netto, "EUR")}</span>
                  </div>
                  {taxGroups.map((g) => (
                    <div key={g.rate} className="flex justify-between">
                      <span className="text-label-secondary">MwSt. {g.rate}%</span>
                      <span>{formatCurrency(g.tax, "EUR")}</span>
                    </div>
                  ))}
                  <div className="flex justify-between font-semibold">
                    <span>Summe brutto</span>
                    <span>{formatCurrency(previewTotals.summe_brutto, "EUR")}</span>
                  </div>
                </div>
              </div>
            </div>
            <DocumentPositionCanvasSidebar />
          </DocumentPositionCanvas>

          {editPosition && isMergedContainerPosition(editPosition) ? (
            <DocumentMergedPositionEditDialog
              open={editLocalId != null}
              position={editPosition}
              editable
              currency="EUR"
              lohnarten={visibleLohnarten}
              kundenId={null}
              auftragId={null}
              onClose={closePositionEditor}
              zIndexClass={DOCUMENT_EDITOR_NESTED_OVERLAY_Z}
              onSave={savePosition}
              onExtractChild={(mergedPosition, childIndex) => {
                let updatedMerged: EditorPosition | null = null;
                setPositions((prev) => {
                  const withDraft = prev.map((p) =>
                    p.localId === mergedPosition.localId ? mergedPosition : p,
                  );
                  const next = extractMergedChildFromPositions(
                    withDraft,
                    mergedPosition.localId,
                    childIndex,
                  );
                  const found = next.find((p) => p.localId === mergedPosition.localId);
                  updatedMerged =
                    found && isMergedContainerPosition(found) ? found : null;
                  return next;
                });
                return updatedMerged;
              }}
            />
          ) : (
            <DocumentPositionEditDialog
              open={editLocalId != null}
              position={editPosition}
              editable
              currency="EUR"
              lohnarten={visibleLohnarten}
              kundenId={null}
              auftragId={null}
              onClose={closePositionEditor}
              zIndexClass={DOCUMENT_EDITOR_NESTED_OVERLAY_Z}
              onSave={savePosition}
            />
          )}
        </div>
      )}
    </Dialog>
  );
}
